import { toLocalDayKey } from "@/lib/date/day-keys";
import type { CentralIconName } from "@/types/central-icon";

/** Nested under plans-billing so it inherits that route's admin guard
 * and keeps "Plans & Billing" selected in the settings sidebar. */
export const CREDIT_BREAKDOWN_HREF = "/settings/plans-billing/credits";

/** The three ways an org consumes credits. Order, colour and label are
 * shared by the chart series, the category bars and the ledger filter so
 * a category reads the same everywhere on the page. Colours come from the
 * KPI palette the sequences surfaces use. */
export const USAGE_CATEGORIES = [
  {
    key: "contact_reveal",
    label: "Contact reveals",
    color: "#f59e0b",
    icon: "IconUser" as CentralIconName,
  },
  {
    key: "export",
    label: "Exports",
    color: "#60a5fa",
    icon: "IconArrowInbox" as CentralIconName,
  },
  {
    key: "ats_sync",
    label: "ATS sync",
    color: "#f472b6",
    icon: "IconArrowsRepeat" as CentralIconName,
  },
] as const;

export type UsageCategoryKey = (typeof USAGE_CATEGORIES)[number]["key"];

/** Non-usage movements. They never appear in the breakdowns, but the
 * ledger below lists them, so the filter needs their labels. */
export const LEDGER_CATEGORIES = [
  ...USAGE_CATEGORIES.map(({ key, label }) => ({ key, label })),
  { key: "allocation", label: "Cycle allocation" },
  { key: "grant", label: "Admin grant" },
  { key: "seats", label: "Seats" },
  { key: "plan_change", label: "Plan change" },
  { key: "reset", label: "Expired credits" },
  { key: "other", label: "Other" },
] as const;

export const ENTRY_TYPES = [
  { key: "debit_reveal", label: "Contact reveal" },
  { key: "refund_reveal", label: "Reveal refund" },
  { key: "debit_export", label: "Export debit" },
  { key: "refund_export", label: "Export refund" },
  { key: "cycle_allocation", label: "Cycle allocation" },
  { key: "cycle_reset", label: "Cycle reset" },
  { key: "seat_add", label: "Seat added" },
  { key: "seat_add_reversal", label: "Seat removed" },
  { key: "plan_change", label: "Plan change" },
  { key: "admin_grant", label: "Admin grant" },
] as const;

const ENTRY_TYPE_LABELS = new Map<string, string>(
  ENTRY_TYPES.map(({ key, label }) => [key, label]),
);

export function entryTypeLabel(entryType: string): string {
  return ENTRY_TYPE_LABELS.get(entryType) ?? humanize(entryType);
}

const CATEGORY_COLORS = new Map<string, string>(
  USAGE_CATEGORIES.map(({ key, color }) => [key, color]),
);

/** Non-usage categories have no series colour of their own; they render
 * in the neutral token so nothing implies they are spend. */
export function categoryColor(category: string): string {
  return CATEGORY_COLORS.get(category) ?? "var(--color-contrast)";
}

function humanize(value: string): string {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function formatCredits(value: number): string {
  return value.toLocaleString();
}

/** Percentages are rendered from the server's 0-1 share so the page and
 * an exported CSV can never round differently.
 *
 * Sub-1% shares keep a decimal so they don't all collapse to "0%", but
 * anything that would round up to 1% or more uses whole numbers — showing
 * "1.0%" beside "1%" made the smaller share read as the larger one. */
export function formatShare(share: number): string {
  if (share <= 0) return "0%";
  const percent = share * 100;
  if (percent < 0.95) return `${percent.toFixed(1)}%`;
  return `${Math.round(percent)}%`;
}

/** Download name for a ledger export: the day it was taken plus the
 * window it covers, so a folder of exports sorts and reads by itself.
 *
 * The window comes from the Date filter, since that is the only thing
 * scoping the file — naming an unbounded export "all-time" is true, but
 * so is naming a bounded one by its bounds. */
export function creditExportFilename(
  bounds: { from?: string; to?: string } = {},
  now = new Date(),
): string {
  const { from, to } = bounds;
  let range = "all-time";
  if (from && to) range = `${toLocalDayKey(from)}-to-${toLocalDayKey(to)}`;
  else if (from) range = `from-${toLocalDayKey(from)}`;
  else if (to) range = `up-to-${toLocalDayKey(to)}`;
  return `credit-ledger-${toLocalDayKey(now)}-${range}.csv`;
}
